export interface QuantifiedOrderLineItem {
  quantity: number;
  currentQuantity: number;
  unfulfilledQuantity: number;
}

export interface OrderLineItemSegment<T> {
  item: T;
  quantity: number;
}

export interface GroupedOrderLineItems<T> {
  unshipped: Array<OrderLineItemSegment<T>>;
  shipped: Array<OrderLineItemSegment<T>>;
  cancelled: Array<OrderLineItemSegment<T>>;
}

export interface OrderCancellationSummary<T> {
  fullyCancelled: boolean;
  partiallyCancelled: boolean;
  cancelled: Array<OrderLineItemSegment<T>>;
}

export type OrderCancellationType = "" | "partial" | "full";

const cancelledOrderStatuses = new Set(["CANCELLED", "CANCELED", "取り消し", "キャンセル"]);

function boundedQuantity(value: number, maximum: number): number {
  if (!Number.isFinite(value)) return maximum;
  return Math.min(Math.max(Math.trunc(value), 0), maximum);
}

export function groupOrderLineItems<T extends QuantifiedOrderLineItem>(
  items: T[],
  orderFulfillmentStatus = "",
): GroupedOrderLineItems<T> {
  const groups: GroupedOrderLineItems<T> = {
    unshipped: [],
    shipped: [],
    cancelled: [],
  };
  const normalizedOrderStatus = orderFulfillmentStatus.trim().toUpperCase();

  for (const item of items) {
    const orderedQuantity = Math.max(0, Math.trunc(item.quantity));
    const currentQuantity = item.currentQuantity >= 0
      ? boundedQuantity(item.currentQuantity, orderedQuantity)
      : orderedQuantity;
    const fallbackUnfulfilled = normalizedOrderStatus === "FULFILLED"
      || normalizedOrderStatus === "FULFILLMENT_NOT_REQUIRED"
      ? 0
      : currentQuantity;
    const unshippedQuantity = item.unfulfilledQuantity >= 0
      ? boundedQuantity(item.unfulfilledQuantity, currentQuantity)
      : fallbackUnfulfilled;
    const shippedQuantity = currentQuantity - unshippedQuantity;
    const cancelledQuantity = orderedQuantity - currentQuantity;

    if (unshippedQuantity > 0) {
      groups.unshipped.push({ item, quantity: unshippedQuantity });
    }
    if (shippedQuantity > 0) {
      groups.shipped.push({ item, quantity: shippedQuantity });
    }
    if (cancelledQuantity > 0) {
      groups.cancelled.push({ item, quantity: cancelledQuantity });
    }
  }

  return groups;
}

export function summarizeOrderCancellation<T extends QuantifiedOrderLineItem>(
  items: T[],
  orderFulfillmentStatus = "",
): OrderCancellationSummary<T> {
  const fullyCancelled = cancelledOrderStatuses.has(orderFulfillmentStatus.trim().toUpperCase());
  const cancelled = groupOrderLineItems(items, orderFulfillmentStatus).cancelled;
  return {
    fullyCancelled,
    partiallyCancelled: !fullyCancelled && cancelled.length > 0,
    cancelled,
  };
}

export function orderCancellationType<T extends QuantifiedOrderLineItem>(
  items: T[],
  orderFulfillmentStatus = "",
): OrderCancellationType {
  const summary = summarizeOrderCancellation(items, orderFulfillmentStatus);
  if (summary.fullyCancelled) return "full";
  if (summary.partiallyCancelled) return "partial";
  return "";
}

export function orderCancellationLabel(
  summary: Pick<OrderCancellationSummary<unknown>, "fullyCancelled" | "partiallyCancelled">,
): "キャンセル" | "一部キャンセル" | "" {
  if (summary.fullyCancelled) return "キャンセル";
  if (summary.partiallyCancelled) return "一部キャンセル";
  return "";
}
